import { useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';

import { trackEvent } from '../../../../helpers/analytics';
import {
  useCreateSharedListViewMutation,
  useUpdateSharedListViewMutation,
  useDeleteSharedListViewMutation,
} from '../../../../services/sharedListViewsV2';

export const useSharedView = ({ list, activeNetwork, currentView, user }) => {
  const [createView, { isLoading: isCreatingView }] = useCreateSharedListViewMutation();
  const [updateView, { isLoading: isUpdatingView }] = useUpdateSharedListViewMutation();
  const [deleteView, { isLoading: isDeletingView }] = useDeleteSharedListViewMutation();

  const sharedView = useMemo(() => {
    const views = list?.views || [];
    return views.find((view) => view.shareable === true);
  }, [list?.views]);

  const viewId = sharedView?.id;
  const isShared = Boolean(sharedView);

  const updateViewWithToast = useCallback(
    async (updateData) => {
      try {
        await updateView(updateData).unwrap();
        toast('Shared view settings updated', { icon: 'success', id: 'shared-view-settings-updated' });
      } catch (error) {
        toast('Failed to update shared view settings', { icon: 'error', id: 'failed-to-update-shared-view-settings' });
        throw error;
      }
    },
    [updateView],
  );

  const enableSharing = useCallback(
    async (getInitialFieldsSerialized) => {
      const initialFields = getInitialFieldsSerialized();

      try {
        await createView({
          sharedListId: list.id,
          data: {
            type: currentView === 'card' ? 'cards_view' : 'table_view',
            shareable: true,
            fieldSettings: initialFields,
            introRequestsEnabled: true,
            introRequestManagerId: user?.id,
          },
        }).unwrap();

        toast('Shared view settings updated', { icon: 'success' });
        trackEvent('contacts:list:save_share_settings', {
          collection_id: activeNetwork.id,
          list_id: list.id,
        });
      } catch (error) {
        toast('Failed to update shared view settings', { icon: 'error' });
        throw error;
      }
    },
    [list.id, activeNetwork.id, currentView, createView, user?.id],
  );

  const disableSharing = useCallback(async () => {
    if (!viewId) return;

    try {
      await deleteView({
        sharedListId: list.id,
        id: viewId,
      }).unwrap();
      toast('Shared view settings updated', { icon: 'success' });
    } catch (error) {
      toast('Failed to update shared view settings', { icon: 'error' });
      throw error;
    }
  }, [viewId, list.id, deleteView]);

  const onLayoutChange = useCallback(
    async ({ value }) => {
      if (!viewId) return;

      try {
        await updateViewWithToast({
          sharedListId: list.id,
          id: viewId,
          data: {
            type: value,
          },
        });

        trackEvent('contacts:list:change_shared_layout', {
          collection_id: activeNetwork.id,
          list_id: list.id,
          layout: value,
        });
      } catch (error) {
        // Error already handled by updateViewWithToast
      }
    },
    [viewId, list.id, activeNetwork.id, updateViewWithToast],
  );

  return {
    sharedView,
    viewId,
    isShared,
    enableSharing,
    disableSharing,
    onLayoutChange,
    updateView: updateViewWithToast,
    isCreatingView,
    isUpdatingView,
    isDeletingView,
  };
};
